import {
  flowGraphSchema,
  successors,
  validateFlow,
  type FlowGraph,
  type FlowIssue,
  type FlowNode,
  type ParamSource,
} from '@cartly/shared';

/** Pure helpers behind the flow builder: editing a graph, and checking it as the user types. */

export type NodeType = FlowNode['type'];
export type Slot = 'next' | 'yes' | 'no' | 'a' | 'b';

const PREFIX: Record<NodeType, string> = {
  send_whatsapp_template: 'send',
  send_whatsapp_text: 'text',
  wait: 'wait',
  condition: 'check',
  split: 'split',
  add_tag: 'tag',
  remove_tag: 'untag',
};

export function newNodeId(graph: FlowGraph, type: NodeType): string {
  const used = new Set(graph.nodes.map((n) => n.id));
  for (let i = 1; ; i++) {
    const id = `${PREFIX[type]}-${i}`;
    if (!used.has(id)) return id;
  }
}

export interface NodeDefaults {
  phoneNumberId: string;
  templateName: string;
  templateLanguage: string;
  params: ParamSource[];
}

/** A new step with valid defaults where the schema allows one. */
export function defaultNode(graph: FlowGraph, type: NodeType, d: NodeDefaults): FlowNode {
  const id = newNodeId(graph, type);
  switch (type) {
    case 'send_whatsapp_template':
      return {
        id,
        type,
        phoneNumberId: d.phoneNumberId,
        templateName: d.templateName,
        templateLanguage: d.templateLanguage,
        params: d.params,
        next: null,
      };
    case 'send_whatsapp_text':
      return { id, type, phoneNumberId: d.phoneNumberId, text: '', next: null };
    case 'wait':
      return { id, type, amount: 1, unit: 'hours', next: null };
    case 'condition':
      return {
        id,
        type,
        match: 'all',
        conditions: [{ field: 'ordersCount', op: 'gte', value: 1 }],
        yes: null,
        no: null,
      };
    case 'split':
      return { id, type, percentA: 50, a: null, b: null };
    case 'add_tag':
    case 'remove_tag':
      return { id, type, tag: '', next: null };
  }
}

export function slotsOf(node: FlowNode): Slot[] {
  if (node.type === 'split') return ['a', 'b'];
  return node.type === 'condition' ? ['yes', 'no'] : ['next'];
}

export function slotTarget(node: FlowNode, slot: Slot): string | null {
  if (node.type === 'split') return slot === 'a' ? node.a : slot === 'b' ? node.b : null;
  if (node.type === 'condition') return slot === 'yes' ? node.yes : slot === 'no' ? node.no : null;
  return slot === 'next' ? node.next : null;
}

function withSlot(node: FlowNode, slot: Slot, to: string | null): FlowNode {
  if (node.type === 'split') {
    if (slot === 'a') return { ...node, a: to };
    if (slot === 'b') return { ...node, b: to };
    return node;
  }
  if (node.type === 'condition') {
    if (slot === 'yes') return { ...node, yes: to };
    if (slot === 'no') return { ...node, no: to };
    return node;
  }
  return slot === 'next' ? { ...node, next: to } : node;
}

export function setLink(graph: FlowGraph, from: string, slot: Slot, to: string | null): FlowGraph {
  return {
    ...graph,
    nodes: graph.nodes.map((n) => (n.id === from ? withSlot(n, slot, to) : n)),
  };
}

/**
 * Adds a step. It becomes the start when the flow is empty, otherwise it is linked after
 * `after` (a node whose first free slot is used) when given.
 */
export function addNode(graph: FlowGraph, node: FlowNode, after: string | null): FlowGraph {
  let next: FlowGraph = { ...graph, nodes: [...graph.nodes, node] };
  if (graph.start === null) return { ...next, start: node.id };
  const source = after ? graph.nodes.find((n) => n.id === after) : undefined;
  if (source) {
    const free = slotsOf(source).find((s) => slotTarget(source, s) === null);
    if (free) next = setLink(next, source.id, free, node.id);
  }
  return next;
}

/** Removes a step and every link to it; the start moves on to its successor. */
export function removeNode(graph: FlowGraph, id: string): FlowGraph {
  const gone = graph.nodes.find((n) => n.id === id);
  if (!gone) return graph;
  const replacement = successors(gone)[0] ?? null;
  const nodes = graph.nodes
    .filter((n) => n.id !== id)
    .map((n) => {
      let out = n;
      for (const slot of slotsOf(n)) {
        if (slotTarget(n, slot) === id) out = withSlot(out, slot, null);
      }
      return out;
    });
  return {
    ...graph,
    start: graph.start === id ? replacement : graph.start,
    nodes,
  };
}

/** A copy of a step with a fresh id and no links, added to the graph. */
export function duplicateNode(
  graph: FlowGraph,
  id: string,
): { graph: FlowGraph; id: string } | null {
  const src = graph.nodes.find((n) => n.id === id);
  if (!src) return null;
  const copyId = newNodeId(graph, src.type);
  let copy = { ...structuredClone(src), id: copyId } as FlowNode;
  for (const slot of slotsOf(copy)) copy = withSlot(copy, slot, null);
  return { graph: { ...graph, nodes: [...graph.nodes, copy] }, id: copyId };
}

export function replaceNode(graph: FlowGraph, node: FlowNode): FlowGraph {
  return { ...graph, nodes: graph.nodes.map((n) => (n.id === node.id ? node : n)) };
}

export interface BuilderIssue {
  nodeId: string | null;
  message: string;
}

const friendly = (message: string): string =>
  /expected string to have|Too small|String must contain|required/i.test(message)
    ? 'This field is required'
    : message;

/** Problems that would make the API reject a save, phrased for the builder. */
export function schemaIssues(graph: FlowGraph): BuilderIssue[] {
  const parsed = flowGraphSchema.safeParse(graph);
  if (parsed.success) return [];
  return parsed.error.issues.map((issue) => {
    const [root, index, ...rest] = issue.path;
    const node = root === 'nodes' && typeof index === 'number' ? graph.nodes[index] : undefined;
    const field = rest.filter((p) => typeof p === 'string').join(' ');
    return {
      nodeId: node?.id ?? null,
      message: field ? `${field}: ${friendly(issue.message)}` : friendly(issue.message),
    };
  });
}

export function structureIssues(graph: FlowGraph): BuilderIssue[] {
  return validateFlow(graph).map((i: FlowIssue) => ({ nodeId: i.nodeId, message: i.message }));
}

/**
 * Everything wrong with the graph right now; empty means it can be saved and published.
 * Structural checks only read ids, links and counts, so they are safe on a graph that still has
 * empty fields.
 */
export function builderIssues(graph: FlowGraph): BuilderIssue[] {
  return [...schemaIssues(graph), ...structureIssues(graph)];
}
